import {
  BinaryExpression,
  FieldOperators,
  GroupFilter,
  GroupOperators,
} from 'src/interfaces/agent.interface';
import { HumanDetectionResult } from 'src/interfaces/call.interface';
import {
  CallCondition,
  PostCallContainsCondition,
  PostCallJsonPropertyCondition,
  UiCondition,
  UnselectedCondition,
  generateConditionId,
  UiConditionType,
} from './types';

export const CALL_HUMAN_DETECTION_OPERAND =
  '{{params.call.human_detection_result}}';
export const POST_CALL_RESULTS_CONTENT_OPERAND =
  '{{params.call.post_call_results.content}}';

// Escape a string for safe embedding inside a JSON string literal
const escapeJsonString = (value: string): string =>
  String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

export const POST_CALL_RESULTS_JSON_PATH_OPERAND = (path: string) =>
  `{{getFromJson({"json": params.call.post_call_results.content, "path": "${escapeJsonString(path)}"})}}`;

const parsePostCallResultsJsonPathOperand = (left: string): string | null => {
  // Matches the JSON string value for "path", allowing escaped quotes
  const regex =
    /getFromJson\(\{"json":\s*params\.call\.post_call_results\.content,\s*"path":\s*"((?:\\.|[^"\\])*)"\}\)\}\}/;
  const match = left.match(regex);
  if (!match) return null;
  const raw = match[1];
  try {
    // Safely unescape using JSON.parse on a quoted string
    return JSON.parse(`"${raw}"`);
  } catch {
    return raw;
  }
};

export const groupFilterToUiConditions = (
  filter?: GroupFilter | null,
): UiCondition[] => {
  if (!filter || !Array.isArray(filter.children)) return [];

  return filter.children.map<UiCondition>((expr) => {
    const id = generateConditionId();

    if (
      expr.left === CALL_HUMAN_DETECTION_OPERAND &&
      expr.operator === FieldOperators.EQ &&
      (expr.right === HumanDetectionResult.NO_HUMAN ||
        expr.right === HumanDetectionResult.HUMAN)
    ) {
      const humanDetectionResult = expr.right as HumanDetectionResult;
      const cond: CallCondition = {
        id,
        type: UiConditionType.CALL,
        humanDetectionResult,
      };
      return cond;
    }

    if (
      expr.left === POST_CALL_RESULTS_CONTENT_OPERAND &&
      expr.operator === FieldOperators.CONTAINS
    ) {
      const cond: PostCallContainsCondition = {
        id,
        type: UiConditionType.POST_CALL_CONTAINS,
        searchString: String(expr.right ?? ''),
      };
      return cond;
    }

    if (
      typeof expr.left === 'string' &&
      expr.left.includes('getFromJson') &&
      expr.left.includes('post_call_results.content')
    ) {
      const path = parsePostCallResultsJsonPathOperand(String(expr.left)) ?? '';
      const cond: PostCallJsonPropertyCondition = {
        id,
        type: UiConditionType.POST_CALL_JSON_PROPERTY,
        path,
        operator: expr.operator as FieldOperators,
        value: String(expr.right ?? ''),
      };
      return cond;
    }

    const fallback: UnselectedCondition = {
      id,
      type: UiConditionType.UNSELECTED,
    };
    return fallback;
  });
};

export const uiConditionsToGroupFilter = (
  uiConditions: UiCondition[],
): GroupFilter | null => {
  const children: BinaryExpression[] = [];

  for (const c of uiConditions) {
    if (c.type === UiConditionType.UNSELECTED) continue;

    if (c.type === UiConditionType.CALL) {
      children.push({
        left: CALL_HUMAN_DETECTION_OPERAND,
        operator: FieldOperators.EQ,
        right: c.humanDetectionResult,
      });
      continue;
    }

    if (c.type === UiConditionType.POST_CALL_CONTAINS) {
      children.push({
        left: POST_CALL_RESULTS_CONTENT_OPERAND,
        operator: FieldOperators.CONTAINS,
        right: c.searchString ?? '', // empty marks incomplete
      });
      continue;
    }

    if (c.type === UiConditionType.POST_CALL_JSON_PROPERTY) {
      const left = POST_CALL_RESULTS_JSON_PATH_OPERAND(c.path ?? ''); // empty path marks incomplete
      const operator = c.operator ?? FieldOperators.EQ; // default; incomplete flagged by empty right or empty path
      children.push({ left, operator, right: c.value ?? '' });
      continue;
    }
  }

  if (children.length === 0) return null;

  return {
    groupOperator: GroupOperators.AND,
    children,
  };
};

export const hasIncompleteConditions = (
  filter?: GroupFilter | null,
): boolean => {
  if (!filter) return false;
  if (!Array.isArray(filter.children)) return false;

  for (const expr of filter.children) {
    // Missing operator is not representable in typing; treat empty right or empty path as incomplete
    const right = expr.right;
    if (right === null || right === undefined) return true;
    if (typeof right === 'string' && right.trim() === '') return true;

    if (
      typeof expr.left === 'string' &&
      expr.left.includes('getFromJson') &&
      expr.left.includes('post_call_results.content')
    ) {
      const path = parsePostCallResultsJsonPathOperand(String(expr.left));
      if (!path || path.trim() === '') return true;
    }
  }

  return false;
};
